import React, { Fragment, useEffect } from 'react';
import { ChartElementType } from './LandingCharts';
import * as d3 from 'd3';
import { formatToDecimals } from '@lib/helpers/formatting';

interface HistogramGraphProps {
	elements: ChartElementType[];
}

const HistogramGraph: React.FC<HistogramGraphProps> = ({ elements }) => {
	const svgWidth = 1600;
	const svgHeight = 250;

	const margins = {
		top: 20,
		bottom: 50,
		left: 50,
		right: 50,
	};

	const xScale = d3
		.scaleBand()
		.domain(elements.map((d) => d.label))
		.range([margins.left, svgWidth - margins.right])
		.padding(0.55);

	const yMax = d3.max(elements, (d) => d.value)!;
	const isZero = yMax === 0;
	const baselineLift = isZero ? 20 : 0;

	const yScale = d3
		.scaleLinear()
		.domain([0, isZero ? 10 : yMax])
		.range([svgHeight - margins.bottom - baselineLift, margins.top]);

	const yTicks = yScale.ticks(yMax > 0 ? 5 : 2);

	const formatTick = (tick: number, yTicks: number[]) => {
		let unit = '';
		if (yTicks.some((t) => t >= 1_000_000_000)) unit = 'mld';
		else if (yTicks.some((t) => t >= 1_000_000)) unit = 'mln';

		if (tick === 0) return '0' + unit;

		const formatted = d3.format('.2~s')(tick);

		if (unit === 'mld') return formatted.replace('G', 'mld');
		if (unit === 'mln') return formatted.replace('M', 'mln');

		return formatted;
	};

	const baselineY = svgHeight - margins.bottom - baselineLift;

	const rectRefs = React.useRef<SVGRectElement[]>([]);
	useEffect(() => {
		rectRefs.current.forEach((rectElement, index) => {
			if (!rectElement) return;
			const element = elements[index];
			if (element.value == null) return;

			const finalY = yScale(element.value);
			let finalHeight = Math.max(0, baselineY - finalY);
			if (finalHeight === 0) finalHeight = 20; // ensure visibility for zero values

			d3.select(rectElement)
				.transition()
				.delay(index * 120)
				.duration(800)
				.ease(d3.easeCubicOut)
				.attr('y', baselineY - finalHeight)
				.attr('height', finalHeight);
		});
	}, [elements, yScale, baselineY]);

	const tooltipRef = React.useRef<HTMLDivElement>(null);
	const onMouseMove = (event: React.MouseEvent<SVGRectElement, MouseEvent>, element: ChartElementType) => {
		if (tooltipRef.current) {
			tooltipRef.current.style.opacity = '1';
			tooltipRef.current.style.left = `${event.clientX - 42}px`;
			tooltipRef.current.style.top = `${event.clientY + 27}px`;
			tooltipRef.current.textContent = `${element.label}: ${formatToDecimals(element.value)} €`;

			if (element.tooltipExtraData?.length > 0) {
				tooltipRef.current.textContent =
					tooltipRef.current.textContent +
					'\n' +
					element.tooltipExtraData.map((data) => `${data.label}: ${formatToDecimals(data.value)} €`).join('\n');
			}
		}
	};
	const onMouseLeave = () => {
		if (tooltipRef.current) {
			tooltipRef.current.style.opacity = '0';
		}
	};

	return (
		<>
			<svg
				width={svgWidth}
				height={svgHeight}
				viewBox={`0 0 ${svgWidth} ${svgHeight}`}
				style={{
					width: '100%',
					height: 'auto',
				}}
			>
				<g
				// transform={`translate(64, 0)`}
				>
					{elements.map((element, index) => {
						if (element.value === null) return null;
						return (
							<Fragment key={element.key}>
								{element.value >= 0 ? (
									<rect
										onMouseMove={(event) => {
											onMouseMove(event, element);
										}}
										onMouseLeave={onMouseLeave}
										x={xScale(element.label)}
										y={baselineY}
										width={xScale.bandwidth()}
										ref={(rectRef) => {
											rectRefs.current[index] = rectRef;
										}}
										height={0}
										fill={element.color}
										style={{
											clipPath: `inset(0px 0px 0px 0px round 12px 12px 0 0)`,
										}}
									/>
								) : null}
								<text
									key={element.key + '-label'}
									x={(xScale(element.label) || 0) + xScale.bandwidth() / 2}
									y={svgHeight - margins.bottom + 24}
									textAnchor="middle"
									fontSize={14}
									fontWeight={700}
								>
									{element.label}
								</text>
								<text
									key={element.key + '-value'}
									x={(xScale(element.label) || 0) + xScale.bandwidth() / 2}
									y={svgHeight - margins.bottom + 40}
									textAnchor="middle"
									fontSize={14}
									fontWeight={700}
								>
									{formatToDecimals(element.value)} €
								</text>
							</Fragment>
						);
					})}
					{yTicks.map((tick, index) => {
						return (
							<g key={tick}>
								{/* {index > 0 && index !== yTicks.length - 1 ? ( */}
								<line
									x1={margins.left * 1.5}
									y1={yScale(tick)}
									x2={svgWidth - 16}
									y2={yScale(tick)}
									stroke="#545454"
									strokeDasharray={index === 0 ? '' : '8,8'}
								/>
								{/* ) : null} */}

								<text x={margins.left} y={yScale(tick) + 4} textAnchor="end" fontSize={12}>
									{formatTick(tick, yTicks)}
								</text>
							</g>
						);
					})}
				</g>
			</svg>
			<div ref={tooltipRef} className="graph--tooltip" />
		</>
	);
};

export default HistogramGraph;
